export type Franchisee = {
  af_number: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  marketing_name?: string | null;
  vanity_url?: string | null;
  site_url?: string | null;
  all_in_one_report?: string | null;
  gmb_link?: string | null;
  webform_emails?: string | null;
  active_ppc?: string | null;
  ppc_callrail?: string | null;
  ppc_lp?: string | null;
  city?: string | null;
  state?: string | null;
  territory_name?: string | null;
  fbc_name?: string | null;
  status?: string | null;
  // Satellite roll-up flags (computed server-side from Zoho + Yext).
  is_satellite?: boolean;
  parent_af?: string | null;
  has_own_office?: boolean;
  shared_satellites?: string[];
};

export type AdMatchTerritory = {
  afSub: string;
  matchAmount: number;
  /** Highest month of last 6 × 12 (col J). May be 0 for new franchisees. */
  annualized?: number;
  /** Monthly matched-lead cap for the Qualified Lead program (derived from annualized). */
  qualifiedLeads?: number;
};

export type QualifiedLeadProgram = {
  leadCost: number;        // A Place for Mom cost per lead ($58)
  matchRate: number;       // 0.5 (franchisor covers 50%)
  perLeadMatch: number;    // $29
  enrollContactName: string;
  enrollContactEmail: string;
  totalLeads: number;      // sum of per-territory caps
  maxMonthlyMatch: number; // totalLeads × perLeadMatch
};

export type AdMatchResponse = {
  af: string;
  found: boolean;
  name?: string;
  matchAmount?: number;
  combinedMatch?: number;
  territories?: AdMatchTerritory[];
  qualifiedLead?: QualifiedLeadProgram;
  source?: string;
  lastUpdated?: string;
};

export type AdAllocation = {
  afSub: string;
  afBase: string;
  franchiseeName: string | null;
  googlePct: number;
  metaPct: number;
  updatedBy: string | null;
  updatedAt: string | null;
};

export type AdAllocationListResponse = { allocations: AdAllocation[] };

export type SocialResponse = {
  af_number: string;
  facebook?: string | null;
  instagram?: string | null;
  youtube?: string | null;
  twitter?: string | null;
  site_url?: string | null;
  scraped_at?: string | null;
  refreshing?: boolean;
};

export type YextListing = {
  publisher: string;
  status: string;
  url?: string;
  screenshotUrl?: string;
};

export type ListingsResponse = {
  entity?: {
    id?: string;
    name?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      region?: string;
      postalCode?: string;
      countryCode?: string;
    };
  } | null;
  summary?: { live?: number; total?: number };
  listings?: YextListing[];
  refreshing?: boolean;
  search?: string;
};

export type Review = {
  authorName?: string;
  text?: string;
  rating?: number;
  relativeTime?: string;
  photoUri?: string;
};

export type ReviewsResponse = {
  af_number: string;
  placeId?: string;
  rating?: number;
  userRatingCount?: number;
  reviews?: Review[];
  synced_at?: string;
  refreshing?: boolean;
};

export type LocationAddressResponse = {
  af: string;
  zoho: {
    businessAddress: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    marketingName: string;
    territoryName: string;
    fullAddress: string;
  };
  yext: {
    fullAddress: string;
    entityId: string;
    name: string;
  };
  matchLevel: 'match' | 'differs' | 'no-yext';
  match: boolean;
  // Present only when matchLevel is 'differs'. Each side's address split into
  // segments; segments with same:false are the parts that diverge and are
  // rendered bold. Concatenating a side's texts reproduces its full address.
  diff: { zoho: AddressSegment[]; yext: AddressSegment[] } | null;
  hasZoho: boolean;
  hasYext: boolean;
};

export type AddressSegment = { text: string; same: boolean };

export type AnalyticsKpis = {
  sessions: number;
  sessionsChange: number;
  newUsers: number;
  newUsersChange: number;
  totalUsers: number;
  totalUsersChange: number;
  engagedSessions: number;
  engagedSessionsChange: number;
  phoneClicks: number;
  phoneClicksChange: number;
  webformSubmissions: number;
  webformSubmissionsChange: number;
  chats: number;
  chatsChange: number;
};

export type AnalyticsResponse = {
  af_number: string;
  // A franchisee with no GA4 data yet gets `{ af_number, empty: true,
  // refreshing: true }` back — a truthy object with no `kpis`. Declaring kpis
  // as always-present is what let WebsiteSubTab destructure it unguarded and
  // crash the whole Organic tab; optional here so the compiler forces the check.
  empty?: boolean;
  kpis?: AnalyticsKpis;
  date_range?: string;
  page_path?: string;
  geoData?: Array<{ city: string; region: string; sessions: number }>;
  popularPages?: Array<{ title: string; newUsers: number; engagedSessions: number }>;
  trafficSources?: Array<{ channel: string; sessions: number }>;
  usersOverTime?: Array<{ week: string; newUsers: number }>;
  refreshing?: boolean;
};

export type RankingKeyword = {
  keyword: string;
  organicRank: number | null;
  inLocalPack: boolean;
  localPackRank: number | null;
};

export type RankingsResponse = {
  market: string;
  state: string;
  h1: string;
  queries: string[];
  keywords: RankingKeyword[];
  locationCode?: string | null;
  refreshing?: boolean;
  message?: string;
};

export type GbpLocation = {
  totalViews: number;
  viewsSearch: number;
  viewsMaps: number;
  activity: number;
  drivingDirections: number;
  phoneCalls: number;
  websiteClicks: number;
  totalViewsChange: number | null;
  viewsSearchChange: number | null;
  viewsMapsChange: number | null;
  activityChange: number | null;
  drivingDirectionsChange: number | null;
  phoneCallsChange: number | null;
  websiteClicksChange: number | null;
};

export type GbpSheetResponse = {
  location: GbpLocation | null;
  dateRange: string;
};

export type GbpNetworkMetric = {
  current: number;
  change: number | null;
  contributingLocations: number;
};

export type RankingsTopPerformer = {
  afNumber: string;
  name: string;
  location: string;
  topThreeCount: number;
  packCount: number;
  bestRank: number;
};

export type RankingsLaggard = {
  afNumber: string;
  name: string;
  location: string;
  daysOpen: number;
  openDate: string;
};

export type RankingsGridCell = {
  row: number;
  col: number;
  lat: number;
  lng: number;
  mapsRank: number | null;
  organicRank: number | null;
};

export type RankingsGridForKeyword = {
  keyword: string;
  cells: RankingsGridCell[];
};

export type RankingsGrid = {
  afNumber: string;
  centerLat: number | null;
  centerLng: number | null;
  gridSize: number;
  halfWidthKm: number;
  keywords: RankingsGridForKeyword[];
  checkedAt: string | null;
  refreshing?: boolean;
};

export type GbpHealthActionItem = {
  priority: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
};

// GET /api/gbp/listing — read straight from the Business Profile API.
export type GbpServiceArea = { name: string; zip: string | null; isCounty: boolean };
export type GbpRelatedPage = { url: string; path: string; h1: string | null };
export type GbpService = {
  label: string;
  description: string | null;
  relatedPages: GbpRelatedPage[];
  cityPageCount: number;
};
export type GbpListing = {
  locationId: string;
  title: string;
  storeCode: string;
  websiteUri: string | null;
  serviceAreas: GbpServiceArea[];
  services: GbpService[];
};
export type GbpListingResponse =
  | { configured: false }
  | {
      configured: true;
      source: 'live' | 'fixture';
      fetchedAt: string;
      listings: GbpListing[];
      zips: string[];
      inventoryPageCount: number;
    };

export type GbpHealth = {
  afNumber: string;
  placeId: string | null;
  displayName: string | null;
  primaryCategory: string | null;
  categoryCount: number;
  hasPhone: boolean;
  hasWebsite: boolean;
  hasHours: boolean;
  hasDescription: boolean;
  rating: number | null;
  reviewCount: number;
  mostRecentReviewDays: number | null;
  photoCount: number;
  businessStatus: string | null;
  score: number;
  actionItems: GbpHealthActionItem[];
  syncedAt: string | null;
  refreshing?: boolean;
};

export type RankingsMapPoint = {
  afNumber: string;
  name: string;
  location: string;
  fbcName: string | null;
  lat: number;
  lng: number;
  packCount: number;
  avgMapsRank: number | null;
  keywordsRanked: number;
  hasGrid: boolean;
  gridCoveredPoints: number;
  gridTotalPoints: number;
  gridBestRank: number | null;
};

export type RankingsNetworkSummary = {
  topPerformers: RankingsTopPerformer[];
  needsAttention: RankingsLaggard[];
  mapPoints: RankingsMapPoint[];
  locationsConsidered: number;
};

export type GbpNetworkSummary = {
  dateRange: string;
  locationsTotal: number;
  locationsWithData: number;
  metrics: {
    totalViews: GbpNetworkMetric;
    viewsMaps: GbpNetworkMetric;
    activity: GbpNetworkMetric;
    drivingDirections: GbpNetworkMetric;
    phoneCalls: GbpNetworkMetric;
  };
};

export type AnalyticsNetworkSummary = {
  dateRange: string;
  locationsTotal: number;
  locationsWithData: number;
  metrics: {
    sessions: GbpNetworkMetric;
    chats: GbpNetworkMetric;
    phoneClicks: GbpNetworkMetric;
    webformSubmissions: GbpNetworkMetric;
  };
};

export type AdsCampaign = { name: string; type: string; status: string };

export type AdsLocation = {
  clicks: number;
  impressions: number;
  conversions: number;
  cost: number;
  ctr: number;
  convRate: number;
  avgCPC: number;
  avgCPM: number;
  costPerConv: number;
  campaigns: AdsCampaign[];
  clicksChange: number | null;
  impressionsChange: number | null;
  conversionsChange: number | null;
  costChange: number | null;
  ctrChange: number | null;
  convRateChange: number | null;
  avgCPCChange: number | null;
  avgCPMChange: number | null;
  costPerConvChange: number | null;
};

export type AdsSheetResponse = {
  location: AdsLocation | null;
  dateRange: string;
  // Only set when location is null: 'no_campaign' = not running paid search,
  // 'no_data_in_window' = flagged as running it, but PMA reported nothing.
  reason?: 'no_campaign' | 'no_data_in_window';
  // End of the PMA reporting window, and its age in days. Null when the window
  // text can't be parsed.
  windowEndDate?: string | null;
  windowAgeDays?: number | null;
};

export type MetaCreative = {
  adId: string;
  adName: string;
  status: string;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  body: string | null;
  title: string | null;
  callToAction: string | null;
  instagramPermalinkUrl: string | null;
  facebookPermalinkUrl: string | null;
};

export type MetaCampaignSummary = {
  id: string;
  name: string;
  status: string;
  objective: string;
};

export type MetaInsights = {
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  cpc: number;
  reach: number;
  frequency: number;
  conversions: number;
  convRate: number;
  costPerConv: number;
};

export type MetaResponse = {
  af_number: string;
  found: boolean;
  campaigns: MetaCampaignSummary[];
  current: MetaInsights;
  previous: MetaInsights;
  changes: {
    impressions: number | null;
    clicks: number | null;
    ctr: number | null;
    spend: number | null;
    cpc: number | null;
    conversions: number | null;
    convRate: number | null;
    costPerConv: number | null;
  };
  creatives: MetaCreative[];
  dateRange: string;
};

export type Call = {
  id: number;
  callrailId?: string;
  trackerId?: string;
  afNumber?: string;
  sourceAf?: string;
  customerName?: string | null;
  customerPhone?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  duration?: number;
  answered?: boolean;
  voicemail?: boolean;
  ivrDetected?: boolean;
  startTime: string;
  source?: string;
  sourceName?: string;
  /** CallRail attribution medium, e.g. 'cpc' or 'direct'. */
  medium?: string;
  /** CallRail tracker name. Only populated when `source` is a raw slug. */
  campaign?: string;
  /**
   * Tracker name resolved from `trackingPhoneNumber` by the server, for rows
   * whose CallRail source is an uninformative slug. See trackerSourceResolver.
   */
  resolvedSource?: string;
  trackingPhoneNumber?: string;
  callrailTags?: string[];
  callrailLeadStatus?: string | null;
  callrailLeadScore?: string | number | null;
  callrailSentiment?: string | null;
  callrailHighlights?: string[];
  speakerPercentAgent?: number | null;
  speakerPercentCustomer?: number | null;
  transcript?: string | null;
  callSummary?: string | null;
  hasRecording?: boolean;
  clientInference?: {
    patientName?: string | null;
    patientAge?: number | null;
    patientRelationship?: string | null;
    callerName?: string | null;
    callerRelationship?: string | null;
    patientCondition?: string | null;
    patientLocation?: string | null;
    careNeeds?: string | null;
    confidence?: number | null;
    modelUsed?: string;
    sourceUsed?: string;
  } | null;
  annotation?: {
    tags?: string[];
    leadStatus?: string;
    notes?: string;
    value?: number;
  } | null;
};

export type CallsListResponse = {
  data: Call[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
};

export type CallStats = {
  totalCalls: number;
  answeredCalls: number;
  missedCalls: number;
  voicemails: number;
  avgDuration: number;
  briefCalls: number;
  topSources: Array<{ source: string; count: number }>;
};

export type FunnelStage = {
  totalCalls?: number;
  answered?: number;
  qualityLeads?: number;
  scheduleRequested?: number;
  scheduleBooked?: number;
  assessmentsBooked?: number;
  interviewsScheduled?: number;
  avgDuration?: number;
  briefCalls?: number;
  missed?: number;
  answerRate?: number;
};

export type FunnelResponse = {
  current: FunnelStage;
  previous: FunnelStage | null;
};

export type TimeseriesPoint = {
  date: string;
  total: number;
  answered: number;
  missed: number;
  ivr: number;
};

export type CallSortField = 'start_time' | 'duration' | 'customer_name' | 'source' | 'af_number';

export type ScorecardRow = {
  afNumbers?: string[];
  afNumber: string;
  name: string;
  location?: string;
  fbcName?: string;
  totalCalls: number;
  answerRate: number;
  qualityLeads: number;
  qualityRate: number;
  booked: number;
  bookingRate: number;
  status: 'red' | 'yellow' | 'green';
  issue?: string;
  assessmentsBooked?: number;
  interviewsScheduled?: number;
  locations?: ScorecardRow[];
  // Satellite roll-up flags (computed server-side from Zoho + Yext).
  isSatellite?: boolean;
  parentAf?: string | null;
  hasOwnOffice?: boolean;
  sharedSatellites?: string[];
};

export type FbcPerformance = {
  fbcName: string;
  fbcZohoId?: string;
  franchiseeCount: number;
  totalCalls: number;
  answerRate: number;
  qualityRate: number;
  booked?: number;
  bookingRate: number;
  assessmentsBooked?: number;
  interviewsScheduled?: number;
};

export type TopBottomRow = {
  afNumber: string;
  name: string;
  location?: string;
  fbcName?: string;
  totalCalls: number;
  answerRate: number;
  booked: number;
  bookingRate: number;
  qualityRate: number;
  issue?: string;
};

export type TopBottomResponse = {
  top: TopBottomRow[];
  bottom: TopBottomRow[];
};

export type ContextView =
  | { kind: 'network' }
  | { kind: 'fbc'; fbcName: string }
  | { kind: 'franchisee'; afNumber: string };

export type GbpTopPerformer = { afNumber: string; name: string; count: number };

export type GbpPoorPerformer = {
  afNumber: string;
  name: string;
  count: number;
  daysOpen: number;
  openDate: string;
};

export type GbpReviewsSummary = {
  topPerformers: GbpTopPerformer[];
  poorPerformers: GbpPoorPerformer[];
};

export type OrganicUnderperformer = {
  afNumber: string;
  name: string;
  sessions: number;
  daysOpen: number;
  openDate: string;
};

export type WebsiteTrafficTopPerformer = {
  afNumber: string;
  name: string;
  sessions: number;
  daysOpen: number;
  openDate: string;
};

export type UserRow = {
  id: number | null;
  email: string;
  name: string | null;
  role: 'admin' | 'fbc' | 'franchisee';
  afNumber?: string | null;
  location?: string | null;
  fbcName?: string | null;
  createdAt?: string | null;
  lastLogin?: string | null;
  hasPassword?: boolean;
  afNumbers?: string[];
  pulseStatus: 'active' | 'pending' | 'not_registered';
  source?: string;
};

export type CallFilters = {
  search: string;
  status: '' | 'answered' | 'missed' | 'voicemail' | 'ivr';
  sentiment: '' | 'positive' | 'negative' | 'neutral';
  dateFrom: string;
  dateTo: string;
};

export type TabId =
  | 'tab-website'
  | 'tab-performance'
  | 'tab-paid'
  | 'tab-calls'
  | 'tab-admatch'
  | 'tab-social'
  | 'tab-welcomehome'
  | 'tab-content'
  | 'tab-chat'
  | 'tab-links';

export const TABS: ReadonlyArray<{
  id: TabId;
  label: string;
  ready: boolean;
  /** When set, only these roles see the tab. Omit = everyone. Mirrors the
   *  app menu's role gate. */
  roles?: Array<UserRow['role']>;
}> = [
  { id: 'tab-website', label: 'Live Website & Reviews', ready: true },
  { id: 'tab-performance', label: 'Metrics', ready: true },
  { id: 'tab-paid', label: 'Paid Performance', ready: true },
  { id: 'tab-calls', label: 'Calls and Leads', ready: true },
  { id: 'tab-admatch', label: 'Ad Match Amount(s)', ready: true },
  { id: 'tab-social', label: 'Social', ready: true },
  { id: 'tab-welcomehome', label: 'WelcomeHome', ready: true },
  { id: 'tab-content', label: 'Content', ready: true },
  // Admin-only for now: the chatbot is still being built and franchisees have
  // no reason to configure it until the rollout reaches them.
  { id: 'tab-chat', label: 'Chat', ready: true, roles: ['admin'] },
  { id: 'tab-links', label: 'Important Links', ready: true },
];

// ── Content delivery ────────────────────────────────────────────────

export type ContentSubmissionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'publish_failed';

export type MyContentSubmission = {
  id: number;
  af_number: string;
  submission_type: 'news_events' | 'blog' | 'both';
  news_headline: string | null;
  blog_headline: string | null;
  status: ContentSubmissionStatus;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  published_at: string | null;
  published_url: string | null;
  publish_error: string | null;
  publish_at: string | null;
};

// Admin's view of a submission in the queue (no full content body — that's loaded on click).
export type AdminContentSubmissionRow = {
  id: number;
  af_number: string;
  marketing_name: string | null;
  site_url: string | null;
  submission_type: 'news_events' | 'blog' | 'both';
  news_headline: string | null;
  blog_headline: string | null;
  status: ContentSubmissionStatus;
  // "Hold for Review": set on a pending row by a reviewer; null when not held.
  held_at: string | null;
  held_by_name: string | null;
  created_at: string;
  approved_at: string | null;
  published_at: string | null;
  publish_error: string | null;
  published_url: string | null;
  submitter_email: string | null;
  submitter_name: string | null;
  on_behalf: number | boolean; // mysql returns 1/0 for boolean expression
  approver_email: string | null;
  approver_name: string | null;
};

export type AdminContentSubmissionDetail = AdminContentSubmissionRow & {
  news_content: string | null;
  blog_content: string | null;
  blog_author: string | null;
  images: Array<{ url: string; name?: string; size?: number }>;
  submitting_on_behalf_user_id: number | null;
  rejection_reason: string | null;
  rejector_email: string | null;
  rejector_name: string | null;
  approved_by_user_id: number | null;
  rejected_at: string | null;
  rejected_by_user_id: number | null;
  published_post_id: number | null;
  published_url: string | null;
  publish_at: string | null;
  seo_metadata: { seoTitle: string; metaDesc: string; slug: string; summary?: string; showSummary?: boolean } | null;
  image_alts: Array<{ alt: string; title: string }> | null;
  // 1 when the submitter attested they hold a photo/video release for everyone
  // pictured. 0 on text-only posts and on anything submitted before 2026-07-21.
  photo_release_confirmed?: number | boolean | null;
  // 1 when the submitter attested they own or are licensed to use the images
  // themselves. 0 on text-only posts and on anything submitted before
  // 2026-08-13.
  image_rights_confirmed?: number | boolean | null;
  // 1 when this franchisee has no previously *published* blog.
  is_first_blog?: number | boolean | null;
  // 1 when marketing has already been told this franchisee needs blog setup.
  blog_setup_acked?: number | boolean | null;
};

// `count` is the pending-review count; `failedCount` is the number of posts
// sitting in publish_failed (drives the Publish Failed badge + banner).
export type ContentPendingCountResponse = { count: number; failedCount?: number };

// ── WelcomeHome ─────────────────────────────────────────────────────

export type WhEffectivenessRow = {
  leadSourceId: number | null;
  leadSourceName: string;
  leadSourceCategory: string;
  leads: number;
  reachedAssessment: number;
  reachedIntake: number;
  reachedStartedService: number;
  closedLost: number;
  avgDaysToCare: number | null;
};

export type WhEffectivenessCommunity = {
  community_id: number;
  af_number: string;
  rows: WhEffectivenessRow[];
};

export type WhEffectivenessResponse = {
  af_number: string;
  window_days: number;
  communities: WhEffectivenessCommunity[];
  synced_at: string | null;
};

export type WhHygieneCommunity = {
  community_id: number;
  af_number: string;
  medianSpeedToLeadBhMinutes: number | null;
  medianSpeedToLeadRawMinutes: number | null;
  sampleSize: number;
  avgActivitiesThroughAssessment: number | null;
};

export type WhHygieneResponse = {
  af_number: string;
  window_days: number;
  communities: WhHygieneCommunity[];
  network: {
    medianSpeedToLeadBhMinutes: number | null;
    medianSpeedToLeadRawMinutes: number | null;
    avgActivitiesThroughAssessment: number | null;
  };
};

// ── Webform Leads (Ninja Forms) ─────────────────────────────────────
export type WebformLead = {
  id: number;
  submission_id: number;
  form_id: number | null;
  form_title: string | null;
  submitted_at: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  extra: Record<string, string> | null;
  site_url: string | null;
};

export type WebformLeadsResponse = {
  af_number: string;
  leads: WebformLead[];
  synced_at: string | null;
};

// ── On Site Content (admin-only page inventory) ─────────────────────
// What is on a franchisee's site today, and how stale it is. Shares a crawl
// with the schema audit for now; `source` is there because WordPress is the
// better feed (real modified dates, no firewall in between).

export type SiteContentPage = {
  url: string;
  /** Territory root stripped: /westfield-morristown-senior-care/alzheimers-care/
   *  renders as /alzheimers-care/. */
  path: string;
  h1: string | null;
  pageType: string | null;
  /** What the site claims. From a sitemap this is Yoast's lastmod, which moves
   *  on any touch — treat as a hint, not a fact, unless source is 'wordpress'. */
  siteModifiedAt: string | null;
  /** When WE last regenerated this page. Null = never. */
  lastRefreshedAt: string | null;
  httpStatus: number | null;
  source: 'sitemap' | 'wordpress' | 'manual';
  wpPostId: number | null;
  /** When a draft for this page was last published to WordPress. */
  lastPublishedAt: string | null;
};

export type SiteContentResponse = {
  af_number: string;
  siteUrl: string | null;
  /** When rows were last written. Null when there are none — not the same as
   *  "never tried". */
  syncedAt: string | null;
  /** When a read last RAN, whether or not it found anything. */
  lastAttemptAt: string | null;
  urlCount: number;
  errorMessage: string | null;
  pages: SiteContentPage[];
  refreshing: boolean;
};

// ── Content refresh runs ────────────────────────────────────────────

export type ContentRunStatus =
  | 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export type ContentRun = {
  id: number;
  afNumber: string;
  briefId: number | null;
  status: ContentRunStatus;
  /** Empty means "the whole site as the inventory currently knows it". */
  targetUrls: string[];
  /** What the admin was shown before confirming. */
  estimatedCostUsd: number | null;
  /** What the worker reported. Kept alongside the estimate so the estimate
   *  stays honest — one nobody ever checks stops being worth showing. */
  actualCostUsd: number | null;
  progress: string | null;
  error: string | null;
  claimedBy: string | null;
  claimedAt: string | null;
  finishedAt: string | null;
  createdAt: string | null;
};

export type ContentRunsResponse = { runs: ContentRun[] };

export type QueueRunResponse = {
  ok: boolean;
  runId: number;
  pageCount: number;
  estimatedCostUsd: number;
  briefId: number | null;
};

// ── Drafted pages (review + edit before publishing) ─────────────────

/** One block of a drafted page body. `type` is fixed — it is what the
 *  Gutenberg conversion switches on downstream — so only text/items are
 *  editable. */
export type DraftBlock = { type: string; text: string; items?: string[] };

export type ContentDraft = {
  id: number;
  runId: number;
  afNumber: string;
  /** The inventory page this draft is for, when it could be matched
   *  confidently. Null is normal: the pipeline drafts by city and carries no
   *  slug, so an unmatched draft is reached from its run instead. */
  url: string | null;
  city: string | null;
  state: string | null;
  idx: number;
  title: string | null;
  metaDescription: string | null;
  blocks: DraftBlock[];
  faq: Array<{ question?: string; answer?: string }>;
  edited: boolean;
  editedAt: string | null;
  createdAt: string | null;
  /** Set by the WordPress publisher's write-back. Null = never published. */
  /** When a human approved this for publishing. Null = not approved. */
  approvedAt: string | null;
  publishedAt: string | null;
  publishedWpPostId: number | null;
  publishedUrl: string | null;
};

export type ContentDraftsResponse = { drafts: ContentDraft[] };

// ── Annual content brief ────────────────────────────────────────────

export type ContentBriefCampaign = {
  id: number;
  slug: string;
  title: string;
  intro: string | null;
  opensAt: string | null;
  closesAt: string | null;
};

export type ContentBriefStatus = 'draft' | 'submitted' | 'used';

export type ContentBrief = {
  id: number;
  /** The raw answer. Fed to the pipeline's differentiator step, which wants
   *  unstructured prose and does the structuring itself. */
  notes: string | null;
  stateLicense: string | null;
  certifications: string | null;
  credentials: string | null;
  awards: string | null;
  memberships: string | null;
  bondedInsured: boolean;
  factsAttested: boolean;
  doNotMention: string | null;
  faqQuestions: string | null;
  status: ContentBriefStatus;
  submittedAt: string | null;
  updatedAt: string | null;
};

export type ContentBriefResponse = {
  campaign: ContentBriefCampaign | null;
  brief: ContentBrief | null;
};

/** One crawled URL in the admin-only schema (JSON-LD) audit. */
export type SchemaPage = {
  url: string;
  /** First <h1> on the page. Collected by the same crawl. */
  h1: string | null;
  lastmod: string | null;
  httpStatus: number | null;
  /** Number of <script type="application/ld+json"> blocks. 0 = no structured data. */
  blockCount: number;
  /** @type values found, flattened out of any @graph wrapper. */
  types: string[];
  rawJsonLd: string | null;
  error: string | null;
};

export type SchemaAuditResponse = {
  af_number: string;
  siteUrl: string | null;
  sitemapUrl: string | null;
  /** ISO timestamp of the last crawl; null when never audited. */
  crawledAt: string | null;
  urlCount: number;
  /** Run-level problem (no sitemap, URL cap hit) — distinct from per-page errors. */
  errorMessage: string | null;
  pages: SchemaPage[];
  refreshing: boolean;
};

// ── Chatbot ─────────────────────────────────────────────────────────
// Two roles, three inquiry types: home_care -> sales, caregiver -> recruiting,
// ltci -> sales. See ~/Project Files/Chatbot Program/Chatbot-Flow-Spec.md.

export type ChatRolloutStatus = 'not_started' | 'ready' | 'pilot' | 'live' | 'paused';

export type ChatSettings = {
  af_number: string;
  enabled: number | boolean;
  /** Admin-set. Keeps a sandbox location visible in the rollout tracker while
   *  marking it as not a real franchise. */
  is_test: number | boolean;
  rollout_status: ChatRolloutStatus;
  caregiver_app_link: string | null;
  backstop_email: string | null;
  fallback_minutes: number;
  fallback_message: string | null;
  /** Stamped when the widget actually loads on the franchise's site — the
   *  evidence behind a "live" status, as opposed to someone asserting it. */
  embed_first_seen_at: string | null;
  embed_last_seen_at: string | null;
  embed_last_seen_url: string | null;
  notes: string | null;
};

export type ChatAgent = {
  id: number;
  user_id: number;
  af_number: string;
  role_sales: number | boolean;
  /** Photo shown on the bot's own messages until a person joins. One per AF. */
  is_initial_face?: number | boolean;
  role_recruiting: number | boolean;
  photo_url: string | null;
  display_name: string | null;
  cell_e164: string | null;
  cell_verified_at: string | null;
  active: number | boolean;
};

export type ChatRolloutSite = {
  af: string;
  name: string;
  location: string;
  siteUrl: string | null;
  isTest: boolean;
  enabled: boolean;
  rolloutStatus: ChatRolloutStatus;
  embedFirstSeenAt: string | null;
  embedLastSeenAt: string | null;
  agents: number;
  notes: string | null;
};

export type ChatRolloutResponse = {
  summary: {
    total: number;
    notStarted: number;
    ready: number;
    pilot: number;
    live: number;
    paused: number;
    seenLive: number;
  };
  sites: ChatRolloutSite[];
};

export type ChatConversationStatus = 'open' | 'agent_engaged' | 'closed' | 'abandoned';
export type ChatStoreInquiry = 'home_care_lead' | 'caregiver_lead' | 'ltci_policy_review' | 'unknown';

/** A conversation as the transcript store returns it (amada-data /v1/chat). */
/** A chatbot conversation where the visitor left contact details. Form fields
 *  only — deliberately no transcript or message preview. */
export type ChatbotLead = {
  id: string;
  inquiry_type: ChatStoreInquiry;
  visitor_name: string | null;
  visitor_email: string | null;
  visitor_phone: string | null;
  visitor_zip: string | null;
  ltci_intent: 'claim' | 'review' | null;
  care_recipient: 'self' | 'loved_one' | null;
  started_at: string;
  status: ChatConversationStatus;
};

export type ChatConversation = {
  id: string;
  af_number: string;
  origin: string;
  inquiry_type: ChatStoreInquiry;
  status: ChatConversationStatus;
  assigned_user_id: number | null;
  assigned_at: string | null;
  assigned_agent?: { user_id: number; display_name: string | null; photo_url: string | null } | null;
  visitor_name: string | null;
  visitor_email: string | null;
  visitor_phone: string | null;
  visitor_zip: string | null;
  ltci_intent: 'claim' | 'review' | null;
  care_recipient: 'self' | 'loved_one' | null;
  started_at: string;
  last_message_at: string | null;
  closed_at: string | null;
  message_count?: number;
  last_message_preview?: string | null;
};

export type ChatMessage = {
  seq: number;
  role: 'visitor' | 'bot' | 'agent';
  body: string;
  author_user_id: number | null;
  node_key: string | null;
  created_at: string;
};

export type ChatConversationListResponse = {
  storeEnabled: boolean;
  conversations: ChatConversation[];
  /** How far back the list reaches. A view window, not a retention policy. */
  windowDays?: number;
  /** True when the cap was hit and older conversations exist beyond it. */
  truncated?: boolean;
};

export type ChatConversationDetail = {
  conversation: ChatConversation;
  messages: ChatMessage[];
  since_seq: number;
  agents: Record<string, { user_id: number; display_name: string | null; photo_url: string | null }>;
};

// ── Franchisee notes (admin-only) ───────────────────────────────────
// Internal notes on an account, reached from the berry-red "Notes" button in
// the franchisee profile header. Franchisees and FBCs have no read path.

export type NoteActor = {
  userId: number;
  name: string | null;
  email: string;
};

export type FranchiseeNote = {
  id: number;
  afNumber: string;
  body: string;
  /** Who wrote it — the byline. Stays fixed no matter who edits later. */
  author: NoteActor;
  /** Who last edited it, or null if never edited. */
  updatedBy: NoteActor | null;
  createdAt: string | null;
  updatedAt: string | null;
  zohoNoteId: string | null;
  zohoSyncedAt: string | null;
  zohoSyncError: string | null;
};

/** Whether notes are actually reaching Zoho. Disabled off prod — dev and
 *  staging have no amada-data key on purpose — and the drawer says so rather
 *  than letting admins assume the sync happened. */
export type ZohoSyncStatus = {
  enabled: boolean;
  reason: string | null;
  /** Zoho has no delete and is the legal record; shown before an admin removes
   *  a note that already reached it. */
  deleteCaveat: string;
};

export type FranchiseeNotesResponse = {
  af_number: string;
  notes: FranchiseeNote[];
  zohoSync: ZohoSyncStatus;
};

export type FranchiseeNoteResponse = {
  note: FranchiseeNote;
  zohoSync: ZohoSyncStatus;
};

// ── GBP review requests ──────────────────────────────────
// A franchisee asks a batch of people for a Google review and logs it here.
// No recipient identities are stored, by design — see
// server/src/routes/gbpReviewRequests.ts.

export type ReviewRequestChannel = 'email' | 'sms' | 'print' | 'in_person' | 'other';

export type ReviewCampaign = {
  id: number;
  label: string;
  channel: ReviewRequestChannel;
  recipients: number | null;
  notes: string | null;
  reviewsAtSend: number | null;
  /** Reviews gained since this send, when both ends are known. */
  reviewsSince: number | null;
  createdAt: string;
};

export type ReviewSendResult = {
  campaignId: number;
  /** Counts only — the recipient list is never returned (Coastal #261). */
  found: number;
  /** True when the sheet had a name column, so the emails were addressed. */
  personalised: boolean;
  sent: number;
  suppressed: number;
  failed: number;
  rejected: number;
  duplicates: number;
};

export type ReviewRequestsResponse = {
  af: string;
  /** Marketing/display name, used in the ready-to-send copy. */
  name: string;
  /** False when the instance lacks the keys the send path requires. */
  sendingConfigured?: boolean;
  /** The franchisee's Google "write a review" link, or null if unconfigured. */
  reviewLink: string | null;
  reviewCount: number | null;
  campaigns: ReviewCampaign[];
};
